import React from 'react';
import { MediaPreview } from '~/components/MediaPreview';
import { getSubmissionAnswer, enums, formatDate, enumValue } from '~/util';

export interface NoteBodyProps {
    note: NoteType;
    gap?: null | GapType | RiskGapType;
    previousNote?: NoteType;
}
export function NoteBody({ note, gap, previousNote }: NoteBodyProps) {
    const noteBody = React.useMemo(() => {
        function hasMedia() {
            return (
                note.note &&
                note?.metadata?.filename &&
                (note.action === 1 || note.action === 4) &&
                note.media
            );
        }

        function hasSubmissionMedia() {
            return (
                note?.metadata?.media &&
                note?.metadata?.media?.length > 0 &&
                note.metadata.media[0]?.uniqid
            );
        }

        function formatMediaObject() {
            return {
                id: note.metadata!.uniqid,
                original_filename: note.metadata!.original_filename,
            };
        }

        function formatSubmissionMediaObject() {
            return {
                id: note.metadata!.media![0].uniqid,
                original_filename: note.metadata!.media![0].original_filename!,
            };
        }

        function hasHccDescription() {
            return !note.note && !note.metadata?.submission_answer && note.metadata?.description;
        }

        function getAnswer() {
            const answer = getSubmissionAnswer(gap, note?.metadata?.submission_answer);
            if (Array.isArray(answer)) {
                return answer.map((x) => `${x.props.children} `);
            }
            return answer;
        }

        switch (note.action) {
            case enumValue(['note', 'action', 'Gap submitted']):
            case enumValue(['note', 'action', 'Review status expired']):
            case enumValue(['note', 'action', 'Task created']):
            case enumValue(['note', 'action', 'Task status changed']):
            case enumValue(['note', 'action', 'Event transferred from source system']):
            case enumValue(['note', 'action', 'Event transferred to Zephyr']):
                return null;
            case enumValue(['note', 'action', 'New eSubmission answer']):
            case enumValue(['note', 'action', 'eSubmission answer changed']):
            case enumValue(['note', 'action', 'eSubmission answer removed']):
                return getAnswer();
            case enumValue(['note', 'action', 'Gap status changed']):
                return enums(['gap', 'status', note.metadata!.status!]);
            case enumValue(['note', 'action', 'eSubmission document renamed']):
                return (
                    <span>
                        {hasMedia() ? (
                            <>
                                {note.note?.replace(`"${note.metadata?.original_filename}".`, '')}
                                &quot;
                                <MediaPreview
                                    media={formatMediaObject()}
                                    gapType={note?.submission?.gap_type}
                                />
                                &quot;.
                            </>
                        ) : (
                            note.note
                        )}
                    </span>
                );
            case enumValue(['note', 'action', 'eSubmission document uploaded']):
                return (
                    <span>
                        Uploaded document:{' '}
                        {hasMedia() ? (
                            <MediaPreview
                                media={formatMediaObject()}
                                gapType={note?.submission?.gap_type}
                            />
                        ) : (
                            note.metadata?.original_filename
                        )}
                    </span>
                );
            case enumValue(['note', 'action', 'eSubmission document removed']):
                return <span>Removed document: {note.metadata?.original_filename}</span>;
            case enumValue(['note', 'action', 'eSubmission submitted']):
                if (hasSubmissionMedia()) {
                    switch (note?.submission?.gap_type) {
                        case enumValue(['gap', 'type', 'FOC Gap']):
                        case enumValue(['gap', 'type', 'Quality Gap']):
                            return (
                                <span>
                                    <MediaPreview
                                        media={formatSubmissionMediaObject()}
                                        gapType={note?.submission?.gap_type}
                                    />{' '}
                                    was submitted.
                                </span>
                            );
                        default:
                            return (
                                <span>
                                    <MediaPreview
                                        media={formatSubmissionMediaObject()}
                                        gapType={note?.submission?.gap_type}
                                    />{' '}
                                    was submitted to the coding team.
                                </span>
                            );
                    }
                }
                if (!note?.submission?.gap_type) {
                    return <span>A submission was processed.</span>;
                }
                switch (note.submission.gap_type) {
                    case enumValue(['gap', 'type', 'Risk Gap']):
                        return <span>An eAttestation submission was processed.</span>;
                    case enumValue(['gap', 'type', 'FOC Gap']):
                        return <span>An eAW submission was processed.</span>;
                    case enumValue(['gap', 'type', 'Quality Gap']):
                        return <span>An eSDS submission was processed.</span>;
                    default:
                        return <span>A submission was processed.</span>;
                }
            case enumValue(['note', 'action', 'Task edited']):
                return (
                    <div className="row">
                        <div className="col-6">
                            <div className="fw-bold">Current Details</div>
                            <div>
                                <span className="fw-bold">Note:</span> {note.metadata?.description}
                            </div>
                            <div>
                                <span className="fw-bold">Due Date:</span>{' '}
                                {formatDate(note.metadata?.due_at)}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="fw-bold">Previous Details</div>
                            <div>
                                <span className="fw-bold">Note:</span>{' '}
                                {previousNote?.metadata?.description}
                            </div>
                            <div>
                                <span className="fw-bold">Due Date:</span>{' '}
                                {previousNote?.metadata?.due_at &&
                                    formatDate(previousNote.metadata.due_at)}
                            </div>
                        </div>
                    </div>
                );
            case enumValue(['note', 'action', 'Referral created']):
                return (
                    <>
                        <div>
                            To: {note.metadata?.to_name ? note.metadata?.to_name : 'Any Provider'}{' '}
                            at {note.metadata?.to_organization_name}
                        </div>
                        {note.metadata?.location_name ? (
                            <div>Location: {note.metadata?.location_name}</div>
                        ) : null}
                    </>
                );
            default:
                break;
        }
        if (hasHccDescription()) {
            return note.metadata?.description;
        }
        if (note.note) {
            return note.note;
        }
        return null;
    }, [note, gap, previousNote]);

    return (
        <div className={`card-body note-text${noteBody === null ? ' collapse' : ''}`}>
            {noteBody}
        </div>
    );
}
